import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import notificationService from '../../services/notificationService';

// Async thunks
export const fetchNotifications = createAsyncThunk(
  'notifications/fetchAll',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await notificationService.getAll(params);
      return response.success ? response.data : rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch notifications');
    }
  }
);

export const fetchUnreadCount = createAsyncThunk(
  'notifications/fetchUnreadCount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await notificationService.getUnreadCount();
      return response.success ? response.data : rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch unread count');
    }
  }
);

export const markNotificationAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (id, { rejectWithValue }) => {
    try {
      const response = await notificationService.markAsRead(id);
      return response.success ? { id, data: response.data } : rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to mark notification as read');
    }
  }
);

export const markAllNotificationsAsRead = createAsyncThunk(
  'notifications/markAllAsRead',
  async (_, { rejectWithValue }) => {
    try {
      const response = await notificationService.markAllAsRead();
      return response.success ? response : rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to mark all as read');
    }
  }
);

export const deleteNotification = createAsyncThunk(
  'notifications/delete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await notificationService.delete(id);
      return response.success ? id : rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete notification');
    }
  }
);

export const deleteAllNotifications = createAsyncThunk(
  'notifications/deleteAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await notificationService.deleteAll();
      return response.success ? response : rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete all notifications');
    }
  }
);

export const bulkActionNotifications = createAsyncThunk(
  'notifications/bulkAction',
  async ({ action, notificationIds }, { rejectWithValue }) => {
    try {
      const response = await notificationService.bulkAction(action, notificationIds);
      return response.success ? { action, notificationIds } : rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to perform bulk action');
    }
  }
);

const notificationSlice = createSlice({
  name: 'notifications',
  initialState: {
    items: [],
    unreadCount: 0,
    pagination: {
      total: 0,
      page: 1,
      limit: 10,
      totalPages: 0
    },
    loading: false,
    error: null
  },
  reducers: {
    clearNotificationError: (state) => {
      state.error = null;
    },
    resetNotifications: (state) => {
      state.items = [];
      state.unreadCount = 0;
      state.pagination = {
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch notifications
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.notifications || [];
        state.pagination = action.payload.pagination || state.pagination;
        state.unreadCount = action.payload.unreadCount || 0;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch unread count
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload.unreadCount || 0;
      })

      // Mark as read
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const notification = state.items.find(n => n.id === action.payload.id);
        if (notification && notification.read === 0) {
          notification.read = 1;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })

      // Mark all as read
      .addCase(markAllNotificationsAsRead.fulfilled, (state) => {
        state.items.forEach(notification => {
          notification.read = 1;
        });
        state.unreadCount = 0;
      })

      // Delete notification
      .addCase(deleteNotification.fulfilled, (state, action) => {
        const notification = state.items.find(n => n.id === action.payload);
        if (notification && notification.read === 0) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
        state.items = state.items.filter(n => n.id !== action.payload);
        state.pagination.total = Math.max(0, state.pagination.total - 1);
      })

      // Delete all
      .addCase(deleteAllNotifications.fulfilled, (state) => {
        state.items = [];
        state.unreadCount = 0;
        state.pagination.total = 0;
      })

      // Bulk action
      .addCase(bulkActionNotifications.fulfilled, (state, action) => {
        const { action: bulkAction, notificationIds } = action.payload;
        if (bulkAction === 'mark_read') {
          let countToDecrease = 0;
          state.items.forEach(notification => {
            if (notificationIds.includes(notification.id) && notification.read === 0) {
              notification.read = 1;
              countToDecrease++;
            }
          });
          state.unreadCount = Math.max(0, state.unreadCount - countToDecrease);
        } else if (bulkAction === 'mark_unread') {
          let countToIncrease = 0;
          state.items.forEach(notification => {
            if (notificationIds.includes(notification.id) && notification.read === 1) {
              notification.read = 0;
              countToIncrease++;
            }
          });
          state.unreadCount += countToIncrease;
        } else if (bulkAction === 'delete') {
          let countToDecrease = 0;
          state.items.forEach(notification => {
            if (notificationIds.includes(notification.id) && notification.read === 0) {
              countToDecrease++;
            }
          });
          state.items = state.items.filter(n => !notificationIds.includes(n.id));
          state.unreadCount = Math.max(0, state.unreadCount - countToDecrease);
          state.pagination.total = Math.max(0, state.pagination.total - notificationIds.length);
        }
      });
  }
});

export const { clearNotificationError, resetNotifications } = notificationSlice.actions;
export default notificationSlice.reducer;
